import { useEffect, useState, useRef, useContext } from "react";
import ReactPlayer from 'react-player/vimeo';
import './interviews.scss'
import { Row, Col, Typography } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { interviews } from './interviews.data.js';
import { useLocation } from 'react-router-dom';
import { UserContext } from '../../contexts/user.context';
import PosthogHelper from '../../helpers/posthog.helper';

const { Text, Paragraph } = Typography

export const Interviews = () => {
  const location = useLocation();
  const { currentUser } = useContext(UserContext)
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const selectedPlayerRef = useRef(null);
  const videoContainerRef = useRef(null);
  const contentContainerRef = useRef(null);
  
  // Initialize selected interview based on URL query parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idFromUrl = params.get('id');
    
    if (idFromUrl) {
      // Find interview with matching ID
      const interview = interviews.find(interview => interview.id === idFromUrl);
      if (interview) {
        setSelectedInterview(interview);
        document.title = `Instalab | ${interview.question}`;
      } else {
        // If no match found, use the first interview
        setSelectedInterview(interviews[0]);
        document.title = `Instalab | ${interview.question}`;
      }
    } else {
      // If no ID in URL, use the first interview
      setSelectedInterview(interviews[0]);
      document.title = `Instalab | ${interviews[0].question}`;
    }
  }, [location.search]);
  
  // Check if device is mobile
  useEffect(() => {
    PosthogHelper.track(currentUser, `[tom] view interviews`, true)

    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 992); // 992px is the lg breakpoint in Ant Design
    };
    
    // Check on mount
    checkIfMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkIfMobile);
    
    // Clean up
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);
  
  // Function to update the height of the content container
  const updateContentHeight = () => {
    if (videoContainerRef.current && contentContainerRef.current && !isMobile) {
      const videoHeight = videoContainerRef.current.offsetHeight;
      contentContainerRef.current.style.height = `${videoHeight}px`;
    } else if (contentContainerRef.current && isMobile) {
      // Reset height on mobile to allow natural height
      contentContainerRef.current.style.height = 'auto';
    }
  };
  
  // Update height on mount and when window resizes
  useEffect(() => {
    updateContentHeight();
    
    // Add resize listener
    window.addEventListener('resize', updateContentHeight);
    
    // Clean up
    return () => {
      window.removeEventListener('resize', updateContentHeight);
    };
  }, [selectedInterview, isMobile]); // Re-run when selected interview changes or mobile state changes
  
  // Handle video end
  const handleVideoEnd = () => {
    // Reset to beginning and pause
    if (selectedPlayerRef.current) {
      selectedPlayerRef.current.seekTo(0);
    }
  };

  // Function to handle interview selection
  const handleInterviewSelect = (interview) => {
    setSelectedInterview(interview);
    document.title = `Instalab | ${interview.question}`;
    
    // Update URL with the selected interview ID
    const params = new URLSearchParams(location.search);
    params.set('id', interview.id);
    window.history.pushState({}, '', `${location.pathname}?${params.toString()}`);
    
    // Scroll to top of page
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    // Scroll to top of content container
    if (contentContainerRef.current) {
      contentContainerRef.current.scrollTop = 0;
    }
  };

  const onInterviewPlay = () => {
    PosthogHelper.track(currentUser, `[tom] play: ${selectedInterview.question}`, true)
  }
  
  return (
    <div className="interviews">
      {selectedInterview && <div className="selected-interview-wrapper">
        <Row gutter={[12, 12]}>
          {/* Video column - responsive spans */}
          <Col xs={24} sm={24} md={24} lg={16} xl={16}>
            <div 
              className="selected-video-container"
              tabIndex="-1"
              ref={videoContainerRef}
            >
              <div className="video-wrapper">
                <ReactPlayer
                  ref={selectedPlayerRef}
                  url={selectedInterview.url}
                  controls={true}
                  width="100%"
                  height="100%"
                  muted={false}
                  playsinline={true}
                  volume={1}
                  onEnded={handleVideoEnd}
                  onPlay={onInterviewPlay}
                  onReady={() => {
                    if (selectedPlayerRef.current) {
                      setTimeout(() => {
                        try {
                          const internalPlayer = selectedPlayerRef.current.getInternalPlayer();
                          if (internalPlayer && typeof internalPlayer.play === 'function') {
                            internalPlayer.play();
                          }
                        } catch (e) {
                          console.error('Error playing on ready:', e);
                        }
                      }, 100);
                    }
                  }}
                  className="selected-video"
                  config={{
                    vimeo: {
                      playerOptions: {
                        responsive: true,
                        autopause: true,
                        autoplay: true,
                        playsinline: true,
                        dnt: true,
                        muted: false,
                        volume: 1,
                        background: false,
                        byline: false,
                        title: false,
                        portrait: false,
                        controls: true,
                        loop: false,
                        playerOptions: {
                          allow: "autoplay; fullscreen; picture-in-picture",
                        }
                      }
                    }
                  }}
                />
              </div>
            </div>
          </Col>
          {/* Content column - responsive spans */}
          <Col xs={24} sm={24} md={24} lg={8} xl={8}>
            <div 
              className={`selected-interview-content ${isMobile ? 'mobile' : ''}`}
              ref={contentContainerRef}
            >
              <Paragraph className="selected-interview-question">
                {selectedInterview.question}
              </Paragraph>
              <Paragraph className="selected-interview-answer">
                {selectedInterview.answer}
              </Paragraph>
            </div>
          </Col>
        </Row>
      </div>}

      <Row gutter={[12, 12]}>
        {interviews.map((interview) => (
          <Col 
            key={`interview-${interview.id}`}
            xs={24} 
            sm={12} 
            md={12} 
            lg={8} 
            xl={8}
          >
            <div 
              className="interview-item"
              onClick={() => {
                handleInterviewSelect(interview)
              }}
            >
              {selectedInterview?.id === interview.id && (
                <div className="selected-interview-indicator">
                  <CheckCircleOutlined className="selected-interview-indicator-icon" /> Selected
                </div>
              )}

              <div 
                className="interview-video-overlay"
              > 
                <div 
                  className="interview-video-thumbnail" 
                  style={{
                    backgroundImage: `url(https://vumbnail.com/${interview.url.split('/').pop()}.jpg)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    cursor: 'pointer'
                  }}
                />

                <Text className="interview-video-duration">
                  {interview.duration}
                </Text>
              </div>
            </div>
          </Col>
        ))}
      </Row>
    </div>
  )
}